import Phaser from 'phaser';
import { AudioManager } from '@/systems/AudioManager';
import { getAudioBus } from '@/systems/AudioBus';
import { getSaveManager, ACTIVE_SLOT } from '@/systems/SaveManager';
import { spriteAtlas, type SpriteManifest } from '@/systems/SpriteAtlas';
import { spriteAnims, type SpriteAnimManifest } from '@/systems/SpriteAnimRegistry';
import { HEROES } from '@/data/heroes';

/**
 * BootScene — earliest scene. Loads bare-minimum assets needed for the
 * Title screen, then hands off.
 */
export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Queue all combat + UI SFX for loading.
    // Files live in public/assets/sfx/ — see TODO(MY-1459) in that README.
    const audio = new AudioManager(this);
    audio.preload();

    // Load the sprite manifest first, then queue individual sprite images.
    // When the manifest JSON arrives, parse it into the atlas and start a second
    // loader pass for each declared sprite. Sprite load failures are handled
    // gracefully: getTexture() returns null for anything not registered.
    this.load.json('sprite-manifest', 'assets/sprites/manifest.json');
    // Animation spritesheets (RD pixel-art sheets) — same deferred-load pattern.
    this.load.json('anim-manifest', 'assets/anims/manifest.json');

    this.load.on(Phaser.Loader.Events.FILE_COMPLETE, (key: string) => {
      if (key === 'sprite-manifest') {
        const data = this.cache.json.get('sprite-manifest') as SpriteManifest;
        spriteAtlas.loadManifest(data);
        const bootSpriteIds = new Set(HEROES.map((h) => h.assetId).filter((id) => id != null));
        for (const id of bootSpriteIds) {
          const entry = spriteAtlas.getEntry(id);
          if (entry) {
            this.load.image(id, `assets/${entry.file}`);
          }
        }
        this.load.start();
      } else if (key === 'anim-manifest') {
        const am = this.cache.json.get('anim-manifest') as SpriteAnimManifest;
        spriteAnims.load(am);
      } else if (spriteAtlas.getEntry(key) !== undefined) {
        spriteAtlas.register(key);
      }
    });
  }

  create(): void {
    // Register a global Phaser animation per loaded sprite sheet so any scene
    // can play `anim_<charId>_<state>`. Idempotent + skips unloaded sheets.
    for (const e of spriteAnims.entries()) {
      if (!this.textures.exists(e.key) || this.anims.exists(e.key)) continue;
      this.anims.create({
        key: e.key,
        frames: this.anims.generateFrameNumbers(e.key, { start: 0, end: e.entry.frames - 1 }),
        frameRate: e.entry.fps,
        repeat: e.entry.loop ? -1 : 0,
      });
    }

    // Restore saved audio settings into the shared bus before any scene starts.
    const slot = getSaveManager().load(ACTIVE_SLOT);
    if (slot) getAudioBus().fromOptions(slot.options);
    this.scene.start('TitleScene');
  }
}
